import React, { useEffect, useRef } from 'react';
import { Trash2, LayoutTemplate } from 'lucide-react';

/**
 * EmptiedSectionPrompt
 * --------------------
 * Asked when moving a slide leaves its old section with nothing in it.
 *
 * A section always renders at least one slide, so taking the last one out of it
 * leaves a blank behind — and a blank slide is, correctly, flagged as missing
 * content. That flag is right in general and wrong here: the author did not
 * forget to fill the slide, they emptied the section on purpose and may well
 * want the section gone too.
 *
 * Rather than guess, this asks. Both answers are ordinary editing operations
 * the author could perform by hand; the prompt only saves them the trip.
 *
 * Deliberately raised *after* the move rather than before it. The move is what
 * the author asked for and it is never in question — blocking it behind a
 * dialog would make a routine drag feel risky, and dismissing the prompt just
 * leaves the deck as it already is.
 */
const EmptiedSectionPrompt = ({ isOpen, sectionTitle, onDeleteSection, onKeepBlank, onClose }) => {
  const keepRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  // Focus the keeping option: it is the one that changes nothing structural, so
  // a stray Enter cannot delete a section.
  useEffect(() => {
    if (isOpen) keepRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="emptied-section-title"
        className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-700 bg-gray-900 shadow-2xl"
      >
        <div className="border-b border-gray-700 px-5 py-4">
          <h2 id="emptied-section-title" className="text-base font-bold text-white">
            {sectionTitle ? `"${sectionTitle}" is now empty` : 'That section is now empty'}
          </h2>
          <p className="mt-1 text-xs text-gray-400">
            You moved its last slide somewhere else. What should happen to the section?
          </p>
        </div>

        <div className="space-y-2 px-4 py-4">
          <button
            type="button"
            ref={keepRef}
            onClick={onKeepBlank}
            className="flex w-full items-start gap-3 rounded-xl border border-gray-700 bg-gray-800 px-4 py-3 text-left hover:border-blue-500/60 hover:bg-gray-700"
          >
            <LayoutTemplate size={18} className="mt-0.5 shrink-0 text-blue-400" />
            <span>
              <span className="block text-sm font-semibold text-gray-100">Keep the section</span>
              <span className="mt-0.5 block text-xs text-gray-400">
                Leaves one blank slide ready for new content. It will not be flagged as
                missing content.
              </span>
            </span>
          </button>

          <button
            type="button"
            onClick={onDeleteSection}
            className="flex w-full items-start gap-3 rounded-xl border border-gray-700 bg-gray-800 px-4 py-3 text-left hover:border-rose-500/60 hover:bg-gray-700"
          >
            <Trash2 size={18} className="mt-0.5 shrink-0 text-rose-400" />
            <span>
              <span className="block text-sm font-semibold text-gray-100">Delete the section</span>
              <span className="mt-0.5 block text-xs text-gray-400">
                Removes it from the deck. The slide you moved is not affected.
              </span>
            </span>
          </button>
        </div>

        <div className="flex justify-end border-t border-gray-700 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            Decide later
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmptiedSectionPrompt;
